import React, { useState } from 'react';
import { X, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

export default function DecisionModal({ request, decisionType, processing, onConfirm, onCancel, calculateRefundAmount }) {
  const [organizerNotes, setOrganizerNotes] = useState('');
  const [error, setError] = useState('');

  const isApproval = decisionType === 'approve';
  const refundAmount = calculateRefundAmount(request);

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!organizerNotes?.trim()) {
      setError('Please provide notes for your decision');
      return;
    }

    if (!isApproval && organizerNotes?.trim()?.length < 20) {
      setError('Please provide a detailed reason for rejection (minimum 20 characters)');
      return;
    }

    setError('');
    onConfirm(organizerNotes);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          isApproval ? 'bg-green-50' : 'bg-red-50'
        }`}>
          <div className="flex items-center gap-3">
            {isApproval ? (
              <CheckCircle className="w-6 h-6 text-green-600" />
            ) : (
              <XCircle className="w-6 h-6 text-red-600" />
            )}
            <h2 className="text-xl font-bold text-gray-900">
              {isApproval ? 'Approve' : 'Reject'} Cancellation Request
            </h2>
          </div>
          <button
            onClick={onCancel}
            disabled={processing}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Request Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Request Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Event:</span>
                <span className="font-medium text-gray-900">{request?.event?.title || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Participant:</span>
                <span className="font-medium text-gray-900">
                  {request?.participant?.fullName || request?.participantName || 'N/A'}
                </span>
              </div>
              {isApproval && (
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="text-gray-600">Refund Amount:</span>
                  <span className="font-bold text-green-700">₺{refundAmount}</span>
                </div>
              )}
            </div>
          </div>

          {/* Cancellation Reason (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              User's Cancellation Reason
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700">
              {request?.cancellationRequestReason || 'No reason provided'}
            </div>
          </div>

          {/* Organizer Notes */}
          <div>
            <label htmlFor="organizerNotes" className="block text-sm font-medium text-gray-700 mb-2">
              {isApproval ? 'Approval Notes (Optional)' : 'Rejection Reason (Required)'}
              <span className="text-red-500 ml-1">{!isApproval && '*'}</span>
            </label>
            <textarea
              id="organizerNotes"
              value={organizerNotes}
              onChange={(e) => {
                setOrganizerNotes(e?.target?.value);
                setError('');
              }}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
              placeholder={
                isApproval
                  ? 'Add any notes about this approval (optional)...'
                  : 'Provide a clear reason for rejection (will be sent to participant)...'
              }
              disabled={processing}
              required={!isApproval}
            />
            {!isApproval && (
              <p className="text-xs text-gray-500 mt-1">
                Minimum 20 characters. This will be sent to the participant.
              </p>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Warning for Approval */}
          {isApproval && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">This action will:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Cancel the participant's registration</li>
                  <li>Free up the event slot for new registrations</li>
                  <li>Send confirmation email to the participant</li>
                  <li>Process refund of ₺{refundAmount}</li>
                </ul>
              </div>
            </div>
          )}

          {/* Warning for Rejection */}
          {!isApproval && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-orange-800">
                <p className="font-medium mb-1">This action will:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Reject the cancellation request</li>
                  <li>Keep the participant registered for the event</li>
                  <li>Send rejection email with your reason to the participant</li>
                  <li>Participant remains responsible for attending</li>
                </ul>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={processing}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className={`flex-1 px-6 py-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isApproval
                  ? 'bg-green-600 hover:bg-green-700' :'bg-red-600 hover:bg-red-700'
              }`}
            >
              {processing ? 'Processing...' : isApproval ? 'Confirm Approval' : 'Confirm Rejection'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}